import { useRef, type ChangeEvent } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { CatalogPhoto } from '../types/api';

function PhotoThumb({
  photo,
  usePhotoUrl,
  onRemove,
  isRemoving,
}: {
  photo: CatalogPhoto;
  usePhotoUrl: (photoId: string) => string | null;
  onRemove: (photoId: string) => void;
  isRemoving: boolean;
}) {
  const url = usePhotoUrl(photo.id);
  return (
    <div className="group relative h-20 w-20 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
      {url && <img src={url} alt="" className="h-full w-full object-cover" />}
      <button
        type="button"
        disabled={isRemoving}
        onClick={() => onRemove(photo.id)}
        className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100 disabled:opacity-60"
        title="Remove photo"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

// Shared multi-photo gallery editor - used for both customer catalog items
// and master catalog items (Perry: "For all catalogue items I need to be
// able to add a description and photo(s)"). The lowest-position photo is the
// "cover" shown everywhere else in the app (list thumbnails, the shop).
export function PhotoGallery({
  photos,
  usePhotoUrl,
  onAdd,
  onRemove,
  isUploading,
  isRemoving,
  maxPhotos = 12,
}: {
  photos: CatalogPhoto[];
  usePhotoUrl: (photoId: string) => string | null;
  onAdd: (file: File) => void;
  onRemove: (photoId: string) => void;
  isUploading?: boolean;
  isRemoving?: boolean;
  maxPhotos?: number;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    onAdd(file);
    e.target.value = '';
  }

  const sorted = [...photos].sort((a, b) => a.position - b.position);

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {sorted.map((photo) => (
          <PhotoThumb
            key={photo.id}
            photo={photo}
            usePhotoUrl={usePhotoUrl}
            onRemove={onRemove}
            isRemoving={!!isRemoving}
          />
        ))}
        {sorted.length < maxPhotos && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleChange}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="flex h-20 w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 text-slate-400 transition-colors hover:border-brand-400 hover:text-brand-600 disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              <span className="text-[10px] font-medium">Add</span>
            </button>
          </>
        )}
      </div>
      <p className="mt-1.5 text-xs text-slate-500">
        PNG, JPEG or WebP, up to 2MB each. The first photo is the cover shown in lists.
      </p>
    </div>
  );
}
